<template>
  <!-- Avatar Upload -->
  <div v-if="avatarUploading">
    <avatar-upload @close="closeAvatarUpload" :avatar="user.avatar" />
  </div>

  <!-- Account deletion -->
  <div class="max-w-md mx-auto p-6 stack-lg" v-else-if="deletionVisible">
    <h2 class="text-warning">
      <span class="mr-2">
        <icon name="WaldigLogo" size="1.5em" :settings="{circle: true}" />
      </span>
      {{ $t('users.settings.delete_account')}}
    </h2>
    <div></div>
    <p>
      <span class="font-medium">{{ $t('general.warning')}}:</span>
      {{ $t('users.settings.delete_warning_message')}}
    </p>
    <p>{{ $t('users.settings.if_sure_delete_press_button') }}</p>
    <input-text
      type="password"
      :label="$t('users.settings.current_password')"
      :value="formData.password"
      @update-value="updateForm('password', $event)"
    />
    <div>
      <wd-button @input="toggleDeletion">Abbrechen</wd-button>
    </div>
    <div>
      <wd-button @input="deleteAccount" warning>{{ $t('users.settings.finally_delete_account') }}</wd-button>
    </div>
  </div>

  <!-- User settings -->
  <div v-else>
    <tabs bright popped css="bg-white">
      <tab @input="defaultSettings = true" bright popped :active="defaultSettings">
        {{
        $t("users.settings.user_settings")
        }}
      </tab>
      <tab @input="defaultSettings = false" bright popped :active="!defaultSettings">
        {{
        $t("users.settings.general_settings")
        }}
      </tab>
    </tabs>
    <div class="max-w-md mx-auto p-6 stack" v-if="defaultSettings">
      <div class="w-full flex justify-center">
        <avatar :avatar="user.avatar" edit @upload="toggleUploading" />
      </div>
      <input-text
        :label="$t('general.username')"
        :value="formData.username || user.username"
        @update-value="updateForm('username', $event)"
        required
      />
      <input-text
        :label="$t('general.email')"
        :value="formData.email || user.email"
        @update-value="updateForm('email', $event)"
      />
      <div>
        <wd-button
          transparent
          @input="passwordChangeOpen = true"
          v-if="!passwordChangeOpen"
        >{{ $t('users.settings.password_change') }}</wd-button>
      </div>
      <div class="stack" v-if="passwordChangeOpen">
        <input-text
          type="password"
          :label="$t('users.settings.old_password')"
          :value="formData.password_old"
          @update-value="updateForm('password_old', $event)"
        />
        <input-text
          type="password"
          :label="$t('users.settings.new_password')"
          :value="formData.password_new1 || user.password_new1"
          @update-value="updateForm('password_new1', $event)"
          :validations="[{ validation: 'length', type: 'min', value: 8 }]"
        />
        <input-text
          type="password"
          :label="$t('users.settings.repeat_password')"
          :value="formData.password_new2 || user.password_new2"
          @update-value="updateForm('password_new2', $event)"
        />
        <div>
          <wd-button
            @input="sendPasswordChange"
            :disabled="!formData.password_old || !formData.password_new1 || !formData.password_new2"
          >{{ $t('users.settings.change_password') }}</wd-button>
        </div>
      </div>
      <input-textarea
        :label="$t('users.settings.description')"
        :value="formData.description || user.description"
        @update-value="updateForm('description', $event)"
        rows="6"
      />
      <div class="text-right">
        <wd-button :sending="sending" @input="sendUserUpdate">{{ $t('general.update_verb')}}</wd-button>
      </div>
      <div>
        <wd-button @input="toggleDeletion" warning>{{ $t('users.settings.delete_account') }}</wd-button>
      </div>
    </div>

    <!-- General settings -->
    <div class="max-w-md mx-auto p-6 stack" v-else>
      <h2 class="text-center">{{ $t('general.general') }}</h2>
      <div class="grid grid-cols-1-auto gap-2">
        <input-range
          :label="$t('users.settings.fontsize')"
          :value="initialFontSize"
          @update-value="setFontsize"
        />
        <div
          class="self-center"
          :class="{'text-lg': fontSize === 'lg', 'text-xl': fontSize === 'xl'}"
        >{{ fontSizeTranslation }}</div>
      </div>
      <div>
        <h2 class="text-center">{{ $t('users.settings.privacy') }}</h2>
      </div>
      <div
        class="text-center font-medium tracking-wide mb-4"
        v-for="field in privacyFields"
        :key="field.id"
      >
        <div>{{ $t('users.settings.' + field.name) }}</div>
        <input-radio
          :value="getUserPrivacy(field.id)"
          :options="privacyOptions"
          @update-value="setPrivacyFormData(field.id, $event)"
        />
      </div>
      <div class="text-right">
        <wd-button :sending="sending" @input="send">{{ $t('general.update_verb') }}</wd-button>
      </div>
    </div>
  </div>
</template>

<script>
const Avatar = require("@/modules/avatars/components/Avatar.vue").default;
const AvatarUpload = require("@/modules/avatars/components/AvatarUpload.vue")
  .default;
const InputText = require("@/modules/input/components/InputText.vue").default;
const InputRange = require("@/modules/input/components/InputRange.vue").default;
const InputRadio = require("@/modules/input/components/InputRadio.vue").default;
const InputTextarea = require("@/modules/input/components/InputTextarea.vue")
  .default;
const Tab = require("@/modules/core/components/Tab.vue").default;
const Tabs = require("@/modules/core/components/Tabs.vue").default;

import FormMixin from "@/mixins/FormMixin";

export default {
  name: "ProfileEdit",
  mixins: [FormMixin],
  data() {
    return {
      user: {},
      avatarUploading: false,
      deletionVisible: false,
      defaultSettings: true,
      passwordChangeOpen: false,
      updateUserFormOptions: {},
      fontSize: "default",
      fontSizeTranslation: this.$t("users.settings.fontsizes.default"),
      initialFontSize: "default",
      privacyOptions: [
        {
          text: "Privat",
          value: "private"
        },
        {
          text: "Freunde",
          value: "friends"
        },
        {
          text: "Öffentlich",
          value: "public"
        }
      ],
      privacyFormData: []
    };
  },
  props: {},
  components: {
    Avatar,
    AvatarUpload,
    InputText,
    InputTextarea,
    InputRange,
    InputRadio,
    Tabs,
    Tab
  },
  methods: {
    sendUserUpdate() {
      this.submit(this.updateUserFormOptions);
    },
    sendPasswordChange() {
      if (this.formData.password_old && this.formData.password_new1) {
        let passwordData = {
          password_old: this.formData.password_old,
          password_new1: this.formData.password_new1,
          password_new2: this.formData.password_new2
        };
        this.submit({
          custom: true,
          async: true,
          type: "users",
          call: "UpdateCurrentPassword",
          data: passwordData,
          action: response => {
            this.popup({
              type: "success",
              title: this.$t("users.settings.password_changed"),
              autoHide: true
            });
            this.goTo("/profile");
            location.reload();
          }
        });
      }
    },
    sendFontsizeUpdate() {
      this.$store.dispatch("auth/setFontsize", this.fontSize);
    },
    updateAvatar(avatar) {
      this.$store.getters["users/api"].Custom.UpdateCurrentAvatar(avatar).then(
        result => {
          this.user = result;
          this.toggleEdit();
        }
      );
    },
    toggleUploading() {
      this.avatarUploading = !this.avatarUploading;
    },
    closeAvatarUpload() {
      this.updateHeader();
      this.toggleUploading();
    },
    updateHeader() {
      this.$store.dispatch("header/update", {
        backbutton: true,
        clear: true,
        elements: [
          {
            value: this.$t("users.settings.edit_profile"),
            css: "tracking-wide"
          }
        ]
      });
    },
    toggleDeletion() {
      this.deletionVisible = !this.deletionVisible;
    },
    async deleteAccount() {
      await this.$store.getters["users/api"].Custom.DeleteCurrent(
        this.formData.password
      )
        .then(() => {
          this.$store.dispatch("auth/logout");
          this.popup({
            type: "alert",
            title: this.$t("users.settings.deleted"),
            text: this.$t("users.settings.see_again")
          });
        })
        .catch();
    },
    getFontsize(Size) {
      switch (Size) {
        case "default":
          this.fontSizeTranslation = this.$t(
            "users.settings.fontsizes.default"
          );
          return "0";
          break;
        case "lg":
          this.fontSizeTranslation = this.$t("users.settings.fontsizes.lg");
          return "1";
          break;
        case "xl":
          this.fontSizeTranslation = this.$t("users.settings.fontsizes.xl");
          return "2";
          break;

        default:
          this.fontSizeTranslation = this.$t(
            "users.settings.fontsizes.default"
          );
          return "0";
          break;
      }
    },
    setFontsize(Size) {
      switch (Size) {
        case "0":
          this.fontSize = "default";
          this.fontSizeTranslation = this.$t(
            "users.settings.fontsizes.default"
          );
          break;
        case "1":
          this.fontSize = "lg";
          this.fontSizeTranslation = this.$t("users.settings.fontsizes.lg");
          break;
        case "2":
          this.fontSize = "xl";
          this.fontSizeTranslation = this.$t("users.settings.fontsizes.xl");
          break;

        default:
          this.fontSize = "default";
          this.fontSizeTranslation = this.$t(
            "users.settings.fontsizes.default"
          );
          break;
      }
      this.formData.fontsize = this.fontSize;
    },
    setPrivacyFormData(Field, Value) {
      let field = {
        field_id: Field,
        type: Value
      };

      let elIdx = this.privacyFormData.findIndex(e => e.field_id === Field);
      this.$delete(this.privacyFormData, elIdx);

      this.privacyFormData.push(field);
    },
    getUserPrivacy(Id) {
      let userPrivacyEntry = this.userPrivacyEntries.find(
        e => e.field_id === Id
      );
      return userPrivacyEntry ? userPrivacyEntry.type : "private";
    },
    async send() {
      if (this.privacyFormData.length > 0) {
        for (let p of this.privacyFormData) {
          await this.submit({
            custom: false,
            async: true,
            type: "userprivacyentry",
            call: "create",
            data: p,
            action: response => {}
          });
        }
      }
      this.privacyFormData = [];

      if (this.formData.fontsize) {
        this.sendUserUpdate();
      }
    }
  },
  computed: {
    privacyFields() {
      return this.$store.getters["privacyfield/privacyFields"];
    },
    userPrivacyEntries() {
      return this.$store.getters["userprivacyentry/userPrivacyEntries"];
    }
  },
  async created() {
    this.user = await this.$store.getters["users/api"].Custom.Current();
    await this.$store.getters["privacyfield/api"].GetAll();
    await this.$store.getters["userprivacyentry/api"].GetAll();

    this.fontSize =
      this.user.fontsize !== undefined ? this.user.fontsize : "default";

    this.initialFontSize = this.getFontsize(this.fontSize);

    //──── Populate formData with existing attributes ────────────────────────────────────────
    let userAttributes = ["username", "email", "description"];
    userAttributes.forEach(e => {
      if (this.user[e] !== undefined) {
        this.formData[e] = this.user[e];
      }
    });

    this.updateUserFormOptions = {
      custom: true,
      async: true,
      type: "users",
      call: "UpdateCurrent",
      action: response => {
        this.popup({
          type: "success",
          title: this.$t("users.settings.profile_edited"),
          autoHide: true
        });
        this.goTo("/profile");
        location.reload();
      }
    };
    this.updateHeader();
  }
};
</script>